// Internal modules
import { appStore } from '@/store';
import { apolloClient, apolloMutation } from '@/lib/apollo';
import {
  onAfterPaymentPlanCreationHandler,
  onAfterPaymentPlanSuccessHandler,
  // @ts-expect-error untyped import
} from './responseHandlers';
import {
  filterFormState,
  serializeFormDataForGuestUpgradeEligible,
  serializeOnboardingPlanData,
} from '../utils';
import { pomaLogout } from '../../PasswordlessOnboarding/utils';
import {
  APPROVE_ONBOARD_PAYMENT_MUTATION,
  CREATE_ONBOARDING_PAYMENT_PLAN_MUTATION,
  PRE_AUTHORIZE_PAYMENT_MUTATION,
} from './mutations';
import {
  features,
  flushLogsAndRedirect,
  getToken,
  isProd,
  logAtomicsTransactionSuccessfulAndLeaveCheckout,
  postToMerchantForOldIncontext,
  redirectToBilling,
  redirectToMerchantOnSuccess,
} from '@/lib';
import { handleApiError } from '@/lib/errors';
import { getFptiFlowName } from '@/lib/features/utils';
import { logger } from '@/lib/logger';
import {
  GUEST_UPGRADEABLE_TO_MEMBER_QUERY,
  WPS_DONE_PAGE_QUERY,
} from '@/lib/graphql/queries';
import { setProgressiveSpinnerType } from '../../Common/Spinner/utils';
import {
  EVENTS,
  logPerformanceWithStartTime as logUserInteractionTime,
  startLogPerformanceTime,
} from '@/lib/logger/cplUtils';
import { isFunction } from '@/lib/utils/typeUtils';
import type {
  ApproveOnboardPaymentMutationMutation,
  ApproveOnboardPaymentMutationMutationVariables,
  CreateOnboardingPaymentPlanMutationMutation,
  CreateOnboardingPaymentPlanMutationMutationVariables,
  GetPasskeyEnrollmentContextQuery,
  GuestUpgradeableToMemberQuery,
  GuestUpgradeableToMemberQueryVariables,
  PreAuthorizePaymentMutationMutation,
  PreAuthorizePaymentMutationMutationVariables,
} from '@/generated/graphql';
import type { ApolloQueryResult } from 'apollo-client';
import type { FetchResult } from 'apollo-link';
import { isBillingTypeSubscriptionV1, isUserActionCommit } from '@/lib/globals';
import { getPasskeyEnrollmentContext } from './getPasskeyEnrollmentContext';
import { GET_PASSKEY_ENROLLMENT_CONTEXT_QUERY } from './queries/getPasskeyEnrollmentContext.gql';
import { LazyPasskeyEnrollmentPage } from '@/pages/PasskeyEnrollmentPage/Components';
import { getLocale } from '@/lib/globals';
import { xpropsOnAuthorizeWithOpenerFallback } from '../util/xpropsOnAuthorizeWithOpenerFallback';

export const preAuthorizePaymentPlan = (): ReturnType<
  typeof approvePayment
> => {
  const token = getToken();

  setProgressiveSpinnerType();

  return apolloClient
    .mutate<
      PreAuthorizePaymentMutationMutation,
      PreAuthorizePaymentMutationMutationVariables
    >({
      mutation: PRE_AUTHORIZE_PAYMENT_MUTATION,
      variables: { token },
    })
    .then(response => {
      const { paymentContingencies } =
        response?.data?.preAuthorizePayment || {};

      if (paymentContingencies?.threeDSContingencyData) {
        const { api: apiActions, ui: uiActions } = appStore.getActions();

        apiActions.setThreeDS2ContingencyData({
          ...paymentContingencies.threeDSContingencyData,
        });
        uiActions.toggleIsFormSubmitting(false);
        uiActions.toggleIsThreeDS2Shown(true);

        return;
      }

      return approvePayment();
    });
};

const approveOnboardPaymentMutationCall = async (
  token: string,
): Promise<FetchResult<ApproveOnboardPaymentMutationMutation>> => {
  const { api: apiState } = appStore.getState();
  const instrumentId = apiState.instrumentId;
  const isBillingAgreement = !!features.get('billingType');
  const fptiFlowName = getFptiFlowName({
    clientFeatures: features.config,
  });

  const response = await apolloMutation<
    ApproveOnboardPaymentMutationMutation,
    ApproveOnboardPaymentMutationMutationVariables
  >({
    mutation: APPROVE_ONBOARD_PAYMENT_MUTATION,
    update: (cache, { data }) => {
      const approveGuestSignupData = data?.approveGuestSignUpPayment;
      if (approveGuestSignupData?.completedPaymentInfo) {
        cache.writeQuery({
          data: { checkoutSession: approveGuestSignupData },
          query: WPS_DONE_PAGE_QUERY,
        });
      }
    },
    variables: {
      instrumentId,
      isBillingAgreement,
      token,
    },
  });
  if (response?.errors?.length) {
    logger.error('approve_guest_sign_up_payment_failure', {
      errors: response.errors,
    });
    throw new Error(response.errors.join(','));
  }

  logUserInteractionTime(fptiFlowName, EVENTS.PROCESS_APPROVE);
  return response;
};

const isGuestUpgradeableToMemberQuery = async (
  token: string,
): Promise<ApolloQueryResult<GuestUpgradeableToMemberQuery>> => {
  const { form: formState } = appStore.getState();
  const filteredFormState = filterFormState(formState);
  const guestUpgradeToMemberRequiredVariables =
    // @ts-expect-error - TODO: type filterFormState
    serializeFormDataForGuestUpgradeEligible(filteredFormState);
  const response = await apolloClient.query<
    GuestUpgradeableToMemberQuery,
    GuestUpgradeableToMemberQueryVariables
  >({
    query: GUEST_UPGRADEABLE_TO_MEMBER_QUERY,
    // @ts-expect-error
    variables: {
      token,
      ...guestUpgradeToMemberRequiredVariables,
    },
  });

  return response;
};

/**
 * @function approvePayment
 * @description This function works as a helper function to approve a
 * given checkout session and redirect back to the merchant if successful
 * @return null
 */
type PaymentPromises =
  | [ReturnType<typeof approveOnboardPaymentMutationCall>]
  | [
      ReturnType<typeof approveOnboardPaymentMutationCall>,
      ReturnType<typeof isGuestUpgradeableToMemberQuery>,
      Promise<void | ApolloQueryResult<GetPasskeyEnrollmentContextQuery>>,
    ];

// Need to get the type for xprops onAuthorize
export const approvePayment = (): Promise<unknown> => {
  const token = getToken();
  const { api: apiState } = appStore.getState();
  const { ui: uiActions } = appStore.getActions();
  const { user: userActions } = appStore.getActions();
  const intent = apiState.cartDetails.intent;
  const isWpsGuestUpgradeEligible = features.isActive(
    'isWpsGuestUpgradeEligible',
  );
  const isPOMAEligible = features.isActive('isPOMAEligible');
  const promises = [
    approveOnboardPaymentMutationCall(token),
  ] as PaymentPromises;
  const isPasswordlessUpgrade = features.isActive('isPasswordlessUpgrade');
  const fptiFlowName = getFptiFlowName({
    clientFeatures: features.config,
  });
  const { value: onboardOption } = appStore.getState().form.onboardOption;
  const { country, locale } = getLocale();

  logUserInteractionTime(fptiFlowName, EVENTS.USER_INTERACTION);
  startLogPerformanceTime(EVENTS.PROCESS_APPROVE);

  setProgressiveSpinnerType();

  if (isWpsGuestUpgradeEligible && !isPOMAEligible) {
    logger.info('batch_guestUpgradeableToMember_query');
    promises[1] = isGuestUpgradeableToMemberQuery(token);
  }

  const isGuest = onboardOption === 'guest';
  const isWps = features.isActive('isWps');
  const isPayNow = isUserActionCommit();

  // Passkey enrollment related flags
  const userState = appStore.getState().user;
  // We are controlling Passkey Enrollment page display based on the feature flag in staging/dev
  // environment using feature flag forcePasskeyEnrollment. We can set
  // xo__forcePasskeyEnrollment=true in the url to enforce passkey in staging/dev environments only
  let isPasskeyAvailable = userState.passkey.isAvailable;
  if (!isProd && features.isActive('forcePasskeyEnrollment')) {
    isPasskeyAvailable = true;
  }

  const setPasskeyIWCContext = userActions.setPasskeyIWCContext;
  const setPasskeyReturnToMerchantUrl =
    userActions.setPasskeyReturnToMerchantUrl;
  const setIsPasskeyPasswordlessFlow = userActions.setIsPasskeyPasswordlessFlow;
  const setShowPasskeyEnrollmentPage = uiActions.setShowPasskeyEnrollmentPage;
  let isUserPasskeyExperimentEligible = false;
  let isPasskeyContextAvailable = false;
  let passkeyEnrollmentQueryPromise;

  logger.info('passkey_eligibility_check_flags', {
    buyerCountry: country,
    isGuest,
    isPasskeyAvailable,
    isPayNow,
    isWps,
    locale,
    onboardOption,
  });

  if (isPasskeyAvailable && !isWps && !isGuest && isPayNow) {
    LazyPasskeyEnrollmentPage.preload();
    // This just marks the eligibility to call the elmo
    // The elmo response itself is logged in the getPasskeyEnrollmentContext call
    logger.info(`passkey_experiment_api_call_eligible_${country}`);

    passkeyEnrollmentQueryPromise = getPasskeyEnrollmentContext({
      query: GET_PASSKEY_ENROLLMENT_CONTEXT_QUERY,
    }).then(
      response => {
        if (response.errors) {
          const { message, name, stack } = response.errors[0];
          logger.warn('PASSKEY_ENROLLMENT_CONTEXT_ERROR', {
            message,
            name,
            stack,
          });
          // We want to resolve the promise after logging error
          // as the user has already on path to complete transaction
          // and we dont want to block it based on passkey context API failure
          return Promise.resolve(response);
        }

        const context = response.data?.passkeyEnrollmentContext?.context;
        const treatments = response.data?.elmoExperiment?.treatments ?? [];
        const passkeyExperiment = treatments.filter(
          experiment =>
            experiment?.experimentName ===
            'FEATURE_CHECKOUT_ONBOARDING_PRESENT_PASSKEY',
        )[0];

        // we need to force elmo experiment response to true on staging/dev environment given that
        // feature flag is set to true. If the isUserPasskeyExperimentEligible variable is not set
        // to true, then passkey enrollment page wont show up.
        if (!isProd && features.isActive('forcePasskeyEnrollment')) {
          isUserPasskeyExperimentEligible = true;
        } else {
          isUserPasskeyExperimentEligible =
            passkeyExperiment?.treatmentName?.startsWith('Trmt_') || false;
        }

        if (context) {
          isPasskeyContextAvailable = true;
          setPasskeyIWCContext(context);
        }
        logger.info(
          `passkey_experiment_eligibility_${
            isUserPasskeyExperimentEligible ? 'eligible' : 'not_eligible '
          }`,
          {
            buyerCountry: country,
            context,
            isUserPasskeyExperimentEligible,
            locale,
          },
        );
        return Promise.resolve(response);
      },
      ({ message, name, stack }) => {
        logger.error('PASSKEY_ENROLLMENT_CONTEXT_CALL_FAILED', {
          message,
          name,
          stack,
        });

        return Promise.resolve();
      },
    );
  } else {
    passkeyEnrollmentQueryPromise = Promise.resolve();
  }

  promises[2] = passkeyEnrollmentQueryPromise;

  // TODO: A lot of conditionals on xprops, should we check once
  // instead of many times and log / skip?
  return Promise.all(promises)
    .then(
      ([
        approveOnboardPaymentMutationResponse,
        guestUpgradeableToMemberResponse,
        // This value is not used as we have a .then in the above block to
        // handle getPasskeyEnrollmentContext promise resolve and reject
        // We just want to wait for this promise to return as well so that
        // there is no race condition between passkey context creation and
        // approveOnboardPayment call
        // NOTE: any error thrown by this promise is handled by catch block
        // associated with promise creation above. Error thrown will not trigger
        // catch block in this chain (And this is what we want, because failure of
        // getPasskeyEnrollmentContext should not result in redirection to generic
        // error or failure of transaction or failure of return to merchant)
        _passkeyEnrollmentContextResponse,
      ]) => {
        // Lot of calls to possibly null values, since this is a TS conversion
        // only adding logging for now and avoid altering prod code
        if (!isFunction(window.xprops?.onAuthorize)) {
          logger.warn('xprops.onAuthorize is not a function', {
            methods: Object.keys(window.xprops || {}),
          });
        }
        if (!isFunction(window.xprops?.getConnectURL)) {
          logger.warn('xprops.getConnectURL is not a function', {
            methods: Object.keys(window.xprops || {}),
          });
        }

        const { buyer, cart, completedPaymentInfo, paymentContingencies } =
          approveOnboardPaymentMutationResponse?.data
            ?.approveGuestSignUpPayment || {};
        if (paymentContingencies?.threeDSContingencyData) {
          const { api: apiActions, ui: uiActions } = appStore.getActions();

          apiActions.setThreeDS2ContingencyData({
            ...paymentContingencies.threeDSContingencyData,
          });
          uiActions.toggleIsFormSubmitting(false);
          uiActions.toggleIsThreeDS2Shown(true);
          return;
        }

        const returnUrl = cart?.returnUrl?.href;
        if (!returnUrl) {
          logger.warn('approve_payment_return_url_missing');
        }

        // Single source of truth for GUEST_SIGNUP.FLOW_END: the approve call
        // just succeeded (any 3DS challenge already returned above), so this
        // covers every branch below (EC, WPS, SPB, passkey, subscription,
        // old incontext) instead of being logged separately per branch.
        logger.metric('GUEST_SIGNUP.FLOW_END');

        const { value: onboardOption } = appStore.getState().form.onboardOption;

        // For the Next generation subscriptions flows, we need to redirect users to
        // billingNodeWeb app in order to activate the subscription. The billing app
        // will handle redirecting users back to the merchant.
        // @NOTE the order here is _IMPORTANT_
        if (isBillingTypeSubscriptionV1()) {
          logAtomicsTransactionSuccessfulAndLeaveCheckout();
          redirectToBilling(onboardOption);
          return;
        }

        if (isFunction(window.xprops?.onAuthorize)) {
          const authorizeData = {
            intent: intent?.toLowerCase(),
            orderID: token,
            payerID: buyer?.userId ?? undefined, // Payer ID of the buyer. Required for onAuthorize - is otherwise optional.
            paymentID: cart?.paymentId ?? undefined, // Payment ID of the transaction. Only present if created via the payments API.
            paymentToken: token,
            returnUrl,
          };

          if (window.xprops && isFunction(window.xprops?.getConnectURL)) {
            return window.xprops
              .getConnectURL({ payerID: buyer?.userId })
              .then(connectURL => {
                logAtomicsTransactionSuccessfulAndLeaveCheckout();
                return flushLogsAndRedirect({ url: connectURL });
              })
              .catch(err => {
                logger.error('xprops_getconnecturl_error', {
                  err: err.stack || err.toString(),
                });
                // `redirectToMerchantOnSuccess` will not be called for SPB.
                // The incontext popup will be closed automatically.
                logAtomicsTransactionSuccessfulAndLeaveCheckout();
                return xpropsOnAuthorizeWithOpenerFallback(authorizeData);
              });
          }

          logger.info('xprops_on_authorize', authorizeData);
          logger.fpti('xprops_on_authorize', {
            state_name: 'billing',
            transition_name: 'process_on_authorize',
          });
          // `redirectToMerchantOnSuccess` will not be called for SPB
          // The incontext popup will be closed automatically.
          logAtomicsTransactionSuccessfulAndLeaveCheckout();
          xpropsOnAuthorizeWithOpenerFallback(authorizeData);
          return;
        }

        // For old checkout.js integration send post message
        const didPostForIncontext = postToMerchantForOldIncontext(returnUrl);
        if (didPostForIncontext) {
          logger.fpti('xprops_legacy_authorize', {
            state_name: 'billing',
            transition_name: 'process_legacy_on_authorize',
          });
          // The old checkout.js incontext integration is a popup that will
          // be closed via `postToMerchantForOldIncontext`.
          // `redirectToMerchantOnSuccess` will not be called.
          logAtomicsTransactionSuccessfulAndLeaveCheckout();
          return;
        }

        /**
         * Logging out for POMA because the access token dropped during passive
         * login has an 8 hour expiration, so we want to remove all cookies
         * except refresh token, which hermes does RT to AT exchange and gets
         * a limited AccessToken
         */
        const isGuest = onboardOption === 'guest';
        const hasReturnUrl = Boolean(returnUrl);
        const isPOMAEligible = features.isActive('isPOMAEligible');

        const isPasswordlessOptionalSignup = features.isActive(
          'passwordlessOptionalSignup',
        );
        const isStreamlinePasswordlessOptionalSignup =
          features.isActive('isStreamlineOptionalExperience') &&
          isPasswordlessOptionalSignup;

        const isWps = features.isActive('isWps');
        const isPasswordlessForcedSignup = features.isActive(
          'passwordlessForcedSignup',
        );
        const isStreamlinePasswordlessForcedSignup =
          features.isActive('isStreamlineForcedExperience') &&
          isPasswordlessForcedSignup;

        const isPasswordlessFlow =
          isPOMAEligible ||
          isPasswordlessForcedSignup ||
          isStreamlinePasswordlessForcedSignup ||
          isStreamlinePasswordlessOptionalSignup;

        // This should also consider flags like isPasskeyContextAvailable,
        // not isWPS, not isGuest, isPayNow
        // isPasskeyContextAvailable is created based on all required flags listed above.
        if (isPasskeyContextAvailable && isUserPasskeyExperimentEligible) {
          setPasskeyReturnToMerchantUrl(returnUrl);
          setIsPasskeyPasswordlessFlow(isPasswordlessFlow);
          setShowPasskeyEnrollmentPage(true);
          logger.info(
            `passkey_enrollment_page_render_triggered_for_${country}_${
              isPasswordlessFlow ? 'passwordless' : 'with_password'
            }`,
          );
          return Promise.resolve();
        }
        // EC flow
        if (isPasswordlessFlow && !isWps) {
          if (isGuest) {
            // Redirect to merchant page
            logger.info('attempting_redirect_to_merchant', {
              hasReturnUrl,
              isPasswordlessFlow,
              isWps,
            });
            return redirectToMerchantOnSuccess(returnUrl);
          }

          // At this point, the onboardOption is signup
          return pomaLogout().then(() => {
            // Redirect to merchant page
            logger.info('attempting_redirect_to_merchant_after_poma_logout', {
              hasReturnUrl,
            });
            return redirectToMerchantOnSuccess(returnUrl);
          });
        }

        // WPS flow
        if (isPasswordlessFlow && isWps) {
          if (isGuest) {
            // Redirect to receipt page
            logger.info('redirect_to_receipt_page', {
              isPasswordlessFlow,
              isWps,
            });
            uiActions.toggleIsFormSubmitting(false);
            uiActions.toggleIsWPSDonePageOpen(true);
            return;
          }

          // At this point, the onboardOption is signup
          return pomaLogout().then(() => {
            // Redirect to merchant page
            logger.info('redirect_to_receipt_after_poma_logout', {
              hasReturnUrl,
              isPasswordlessFlow,
              isWps,
            });
            uiActions.toggleIsFormSubmitting(false);
            uiActions.toggleIsWPSDonePageOpen(true);
            return;
          });
        }

        /**
         * Checking the returned backend boolean for an eligible guest upgrade
         * for WPS flow
         */
        if (isWpsGuestUpgradeEligible) {
          if (
            guestUpgradeableToMemberResponse?.data?.isGuestUpgradeableToMember
          ) {
            logger.info('guestUpgradeableToMember_response_present');
            uiActions.toggleIsFormSubmitting(false);

            isPasswordlessUpgrade
              ? uiActions.toggleIsPasswordlessUpgradeOpen(true)
              : uiActions.toggleIsUpgradeInterstitialOpen(true);
            return;
          }
          logger.info(
            'guestUpgradeableToMember_response_not_present_or_false',
            {
              isGuestUpgradeableToMember:
                guestUpgradeableToMemberResponse?.data
                  ?.isGuestUpgradeableToMember,
            },
          );
          logger.fpti('wps_upgrade_interstitial_not_shown_backend_error', {
            state_name: 'Create_account',
            transition_name: 'wps_upgrade_interstitial_not_shown_backend_error',
          });
        }

        /**
         * Checking for WPS flag to prevent merchant redirect and
         * show the WPSDonePageInterstitial instead.
         */
        if (isWps && completedPaymentInfo) {
          uiActions.toggleIsWPSDonePageOpen(true);
          return;
        }

        // Redirect to merchant page
        logger.info('attempting_redirect_to_merchant', { hasReturnUrl });
        return redirectToMerchantOnSuccess(returnUrl);
      },
    )
    .catch((err: Error) => {
      uiActions.toggleIsFormSubmitting(false);
      handleApiError(err);
    });
};

export const createOnboardingPaymentPlan = (
  /** type when filterFormState method is typed */ data: unknown,
): Promise<FetchResult<CreateOnboardingPaymentPlanMutationMutation>> => {
  // @ts-expect-error - TODO: type filterFormState
  const variables = serializeOnboardingPlanData(data);

  return apolloMutation<
    CreateOnboardingPaymentPlanMutationMutation,
    CreateOnboardingPaymentPlanMutationMutationVariables
  >({
    mutation: CREATE_ONBOARDING_PAYMENT_PLAN_MUTATION,
    // @ts-expect-error
    variables,
  })
    .then(onAfterPaymentPlanCreationHandler)
    .then(onAfterPaymentPlanSuccessHandler);
};
